import OneSignal from 'react-onesignal';

/**
 * Initializes OneSignal with the configured app ID and settings.
 * Sets up notification button with custom text and allows localhost for development.
 *
 * @throws {Error} When OneSignal app ID is not configured
 */
export const initOneSignal = async () => {
  if (!process.env.REACT_APP_ONESIGNAL_APP_ID) {
    throw new Error('OneSignal app ID is not set');
  }

  console.info('OneSignal: Feature flag enabled, initializing...');

  try {
    await OneSignal.init({
      appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
      notifyButton: {
        enable: true,
        prenotify: true,
        showCredit: false,
        text: {
          'tip.state.unsubscribed': 'Subscribe to notifications',
          'tip.state.subscribed': 'You are subscribed to notifications',
          'tip.state.blocked': 'Please enable notifications',
          'message.prenotify': 'Click to subscribe to notifications',
          'message.action.subscribing': 'Subscribing...',
          'message.action.subscribed': 'Thanks for subscribing!',
          'message.action.resubscribed': 'You are now subscribed to notifications',
          'message.action.unsubscribed': 'You will no longer receive notifications',
          'dialog.main.title': 'Manage your notifications',
          'dialog.main.button.subscribe': 'Subscribe',
          'dialog.main.button.unsubscribe': 'Unsubscribe',
          'dialog.blocked.title': 'Unblock Notifications',
          'dialog.blocked.message': 'Follow these instructions to allow notifications:',
        },
      },
      allowLocalhostAsSecureOrigin: true,
    });
    console.info('OneSignal: Initialization successful');
  } catch (error) {
    console.error('OneSignal: Initialization failed:', error);
  }
};

/**
 * Logs a user into OneSignal with their unique identifier.
 * This associates the user's push subscription with their account.
 *
 * @param uid - The user's unique identifier
 */
export const loginOneSignal = (uid: string) => {
  OneSignal.login(uid)
    .then(() => {
      console.info('OneSignal.login success');
    })
    .catch((error: Error) => {
      console.error('OneSignal.login error', error);
    });
};

/**
 * Opts the user into push notifications.
 * This will trigger the browser's permission prompt if not already granted.
 *
 * @returns Promise that resolves when the opt-in process completes
 */
export const pushSubscriptionOptin = async (): Promise<void> => {
  try {
    await OneSignal.User.PushSubscription.optIn();
  } catch (error) {
    console.error('Push subscription optin failed:', error);
  }
};

/**
 * OneSignal API Reference
 *
 * Common OneSignal properties and methods for checking user notification status:
 *
 * @example
 * // Check if user has granted permission
 * OneSignal.Notifications.permission
 *
 * @example
 * // Check if SDK is initialized
 * OneSignal.initialized
 *
 * @example
 * // Check if user has opted in to push notifications
 * OneSignal.User.PushSubscription.optedIn
 *
 * @example
 * // Check if user has opted out of push notifications
 * OneSignal.User.PushSubscription.optedOut
 *
 * @example
 * // Check if user has blocked push notifications
 * OneSignal.User.PushSubscription.blocked
 */
